import React, { useContext, useMemo, useState, memo } from 'react';

import { Avatar, Button, Modal, Space, Text } from 'components';
import { Table } from '@bb/elements-web';
import styled from 'styled-components';
import { format } from 'date-fns';
import { supraSubscriptionTypeToLabelMap } from '@bb/domain/dist/types';
import { Flex, FlexAlign, SubscriptionButton } from '../styles';
import MLSUserPageContext from '../MLSUserPageContext';

const SubscriptionHistoryModal = () => {
  const { subscriptions } = useContext(MLSUserPageContext);

  const [isOpen, setIsOpen] = useState(false);

  const subscriptionHistory = useMemo(() => {
    const supraSubscriptionHistory = subscriptions.supra.history;

    if (!supraSubscriptionHistory || supraSubscriptionHistory.length < 1)
      return [];

    const updatedHistory = supraSubscriptionHistory
      ?.sort((a, b) => {
        const dateCompare =
          new Date(b.date).getTime() - new Date(a.date).getTime();
        // Some entries have same date. For such cases, later index must appear before the later.
        return dateCompare === 0 ? -1 : dateCompare;
      })
      .map(({ userName, notes, date, type }) => ({
        key: date,
        date: format(new Date(date), 'yyyy-MM-dd').toString(),
        notes: `${notes} ${
          type ? `(${supraSubscriptionTypeToLabelMap[type]})` : ''
        }`,
        userName,
        service: 'Supra System',
      }));

    return updatedHistory;
  }, [subscriptions]);

  const openModal = () => {
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a: any, b: any) => {
        const dateCompare =
          new Date(b.date).getTime() - new Date(a.date).getTime();
        // Some entries have same date. For such cases, later index must appear before the later.
        return dateCompare === 0 ? -1 : dateCompare;
      },
    },
    {
      title: 'Service',
      dataIndex: 'service',
      key: 'service',
    },
    {
      title: 'Description',
      dataIndex: 'notes',
      key: 'notes',
    },
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
      render: (userName: string) => {
        const [firstName, lastName] = userName.split(' ');
        return (
          <Flex>
            <FlexAlign>
              <Avatar user={{ firstName, lastName }} />
              <Text style={{ marginLeft: '5px' }}>{userName}</Text>
            </FlexAlign>
          </Flex>
        );
      },
    },
  ];

  return (
    <>
      <SubscriptionButton
        onClick={openModal}
        data-cy="view-subscription-history-btn"
      >
        View Subscription History
      </SubscriptionButton>
      <Modal
        centered
        open={isOpen}
        onCancel={closeModal}
        footer={false}
        width="fit-content"
      >
        <ModalContainer>
          <Table
            dataCy="subscription-history-table"
            dataSource={subscriptionHistory}
            columns={columns}
            // showSorterTooltip={{ target: 'sorter-icon' }}
            pagination={{
              position: ['bottomRight'],
              showSizeChanger: false,
            }}
          />
        </ModalContainer>
      </Modal>
    </>
  );
};

export default SubscriptionHistoryModal;

const ModalContainer = styled.div`
  margin-top: 20px;
`;
